import React, { useEffect, useMemo, useState } from 'react';
import { AwardIcon, CheckCircleIcon, PlusIcon, SearchIcon, SparklesIcon, LockIcon, GiftIcon, UserIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { Select } from '../ui/Select';
import { useData } from '../../contexts/DataContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../utils/supabase';
import { getToday, formatDate } from '../../utils/helpers';
interface LoyaltyCard {
  id: string;
  code: string;
  patient_id: string;
  service_ids: string[];
  status: 'activa' | 'canjeada' | 'cancelada';
  earned_at: string;
  redeemed_at?: string | null;
  redeemed_appointment_id?: string | null;
}
const VISITS_REQUIRED = 7;
export function Loyalty() {
  const {
    patients,
    appointments,
    services
  } = useData();
  const {
    canCreate,
    isReadOnly
  } = usePermissions();
  const {
    showToast
  } = useToast();
  const [cards, setCards] = useState<LoyaltyCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [patientId, setPatientId] = useState('');
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [earnedAt, setEarnedAt] = useState(getToday());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'todas' | 'activa' | 'canjeada' | 'cancelada'>('todas');
  const loadCards = async () => {
    setLoading(true);
    const {
      data,
      error
    } = await supabase.from('loyalty_cards').select('*').order('earned_at', {
      ascending: false
    });
    if (error) {
      showToast({
        type: 'error',
        title: 'Módulo pendiente de activar',
        message: 'Ejecuta la migración SQL de tarjetas de fidelidad en Supabase.'
      });
      setCards([]);
    } else {
      setCards((data || []) as LoyaltyCard[]);
    }
    setLoading(false);
  };
  useEffect(() => {
    loadCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Realtime: sincronizar tarjetas de fidelidad entre usuarios ──────────
  useEffect(() => {
    const channel = supabase.channel('loyalty-realtime').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'loyalty_cards'
    }, () => loadCards()).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const completedVisits = (id: string) => appointments.filter(appointment => appointment.patientId === id && appointment.status === 'completada').length;
  const cardsForPatient = (id: string) => cards.filter(card => card.patient_id === id);
  const availableCycles = (id: string) => Math.floor(completedVisits(id) / VISITS_REQUIRED);
  const canIssue = (id: string) => availableCycles(id) > cardsForPatient(id).length;
  const serviceName = (id: string) => services.find(service => service.id === id)?.name || 'Servicio eliminado';
  const LOYALTY_ALLOWED_SERVICE_NAMES = ['masaje relax', 'detox facial', 'facial detox', 'pediluvio'];
  const LOYALTY_ALLOWED_SERVICE_IDS = ['c434251a-08a7-44df-a678-c1534c3c4036',
  // Facial Detox
  '4cbe7081-4a7e-4ea3-a1d8-c0a92f7cb2f0' // Pediluvio
  ];
  const loyaltyEligibleServices = useMemo(() => services.filter(service => LOYALTY_ALLOWED_SERVICE_IDS.includes(service.id) || LOYALTY_ALLOWED_SERVICE_NAMES.includes(service.name.trim().toLowerCase())), [services]);
  const eligiblePatients = useMemo(() => patients.filter(patient => canIssue(patient.id)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [patients, appointments, cards]);
  const openForm = () => {
    setPatientId(patients[0]?.id || '');
    setServiceIds([]);
    setEarnedAt(getToday());
    setIsFormOpen(true);
  };
  const selectService = (id: string) => {
    setServiceIds([id]);
  };
  const createCard = async () => {
    if (!patientId) {
      showToast({
        type: 'error',
        title: 'Selecciona un paciente',
        message: 'Debes elegir el paciente para el cual se emitirá la tarjeta.'
      });
      return;
    }
    if (serviceIds.length !== 1) {
      showToast({
        type: 'error',
        title: 'Selecciona un servicio',
        message: 'La tarjeta debe tener exactamente un servicio gratuito seleccionado.'
      });
      return;
    }
    const code = `FID-${crypto.randomUUID().slice(0, 8).toUpperCase()}`;
    const {
      error
    } = await supabase.from('loyalty_cards').insert({
      code,
      patient_id: patientId,
      service_ids: serviceIds,
      status: 'activa',
      earned_at: earnedAt
    });
    if (error) {
      showToast({
        type: 'error',
        title: 'No se pudo crear',
        message: error.message
      });
      return;
    }
    showToast({
      type: 'success',
      title: 'Tarjeta creada',
      message: 'El paciente ya puede canjear el servicio al completar una cita.'
    });
    setIsFormOpen(false);
    await loadCards();
  };
  const readOnly = isReadOnly('loyalty');
  // KPIs
  const kpis = useMemo(() => ({
    active: cards.filter(c => c.status === 'activa').length,
    redeemed: cards.filter(c => c.status === 'canjeada').length,
    eligible: eligiblePatients.length
  }), [cards, eligiblePatients]);
  const statusTabs = [{
    key: 'todas',
    label: 'Todas'
  }, {
    key: 'activa',
    label: 'Activas'
  }, {
    key: 'canjeada',
    label: 'Canjeadas'
  }, {
    key: 'cancelada',
    label: 'Canceladas'
  }];
  const filteredCards = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return cards.filter(card => {
      if (statusFilter !== 'todas' && card.status !== statusFilter) return false;
      if (!term) return true;
      const patient = patients.find(p => p.id === card.patient_id);
      return card.code.toLowerCase().includes(term) || patient?.name.toLowerCase().includes(term) || card.service_ids.some(id => serviceName(id).toLowerCase().includes(term));
    });
  }, [cards, statusFilter, searchTerm, patients, services]);
  const statusMeta = (status: LoyaltyCard['status']) => {
    switch (status) {
      case 'activa':
        return {
          label: 'Activa',
          variant: 'success' as const
        };
      case 'canjeada':
        return {
          label: 'Canjeada',
          variant: 'primary' as const
        };
      default:
        return {
          label: 'Cancelada',
          variant: 'danger' as const
        };
    }
  };
  return <div className="space-y-6" data-id="element-3977" data-mp="12n5uyhjzgbq0">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl shadow-xl text-white" style={{
      background: 'linear-gradient(135deg,#064e3b,#047857,#065f46)'
    }} data-id="element-3978" data-mp="203v30szj0a7u">
        
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px)'
      }} data-id="element-3979" data-mp="23lskub5lldm2" />
        
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-3980" data-mp="2hks7xedgpkg4" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-3981" data-mp="2mitre0esvls4" />
        <div className="relative z-10 p-6 pl-7" data-id="element-3982" data-mp="2qk5aro72zarv">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5" data-id="element-3983" data-mp="2qiu7rqlgm71t">
            <div data-id="element-3984" data-mp="30b06rka4poqi">
              <div className="flex items-center gap-3 mb-2" data-id="element-3985" data-mp="30bjde4ubwqp8">
                <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-3986" data-mp="30d475s8tk5m1">
                  <AwardIcon className="w-6 h-6 text-white" data-id="element-3987" data-mp="35804n7wtrl6j" />
                </div>
                <h1 className="text-2xl font-bold text-white" data-id="element-3988" data-mp="3a9fo4vjctjmt">
                  Tarjetas de Fidelidad
                </h1>
              </div>
              <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-3989" data-mp="3qlb9ptg71854">
                Cada 7 visitas completadas ("6+1") habilitan un servicio
                gratis, a elegir entre 3 opciones seleccionadas al emitir la
                tarjeta.
                {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-3990" data-mp="3eisepr9qcf1">
                    Solo lectura
                  </span>}
              </p>
            </div>
            <div className="flex gap-3" data-id="element-3991" data-mp="37sqp0zane9ce">
              {canCreate('loyalty') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3993" data-mp="3gvhzavrsez2g" />} onClick={openForm} disabled={patients.length === 0} className="bg-white text-emerald-800 hover:bg-emerald-50 shadow-lg shadow-emerald-900/20" data-id="element-3992" data-mp="3lo1e1w27o08u">
                
                  Crear tarjeta
                </Button>}
            </div>
          </div>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-3994" data-mp="18f8iucy0kyhl">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/15" data-id="element-3995" data-mp="18gtgwcjjezk7">
              <p className="text-xs text-white/70 font-medium" data-id="element-3996" data-mp="upevjz25qm9z">Activas</p>
              <p className="text-xl font-bold text-white" data-id="element-3997" data-mp="znzwk0p8h9zh">{kpis.active}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/15" data-id="element-3998" data-mp="1nc9dopbi59ka">
              <p className="text-xs text-white/70 font-medium" data-id="element-3999" data-mp="1ree5diie3w4a">Canjeadas</p>
              <p className="text-xl font-bold text-white" data-id="element-4000" data-mp="1delglsqfbpyy">{kpis.redeemed}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/15 col-span-2 md:col-span-1" data-id="element-4001" data-mp="1idfwomx0b0fd">
              <p className="text-xs text-white/70 font-medium flex items-center gap-1" data-id="element-4002" data-mp="1ywtn1d66qy5">
                <SparklesIcon className="w-3 h-3" data-id="element-4003" data-mp="6ysly3ci8txv" /> Pacientes elegibles
              </p>
              <p className="text-xl font-bold text-white" data-id="element-4004" data-mp="101ol3raxkvm3">{kpis.eligible}</p>
            </div>
          </div>
        </div>
      </div>

      {readOnly && <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg text-emerald-700 dark:text-emerald-300 text-sm flex items-center gap-2" data-id="element-4005" data-mp="14zzwi6gvje6f">
          <LockIcon className="w-4 h-4" data-id="element-4006" data-mp="v9592yrw68je" />
          <span data-id="element-4007" data-mp="haykk98u1kwe">
            Solo tienes permisos de lectura. No puedes crear tarjetas de
            fidelidad.
          </span>
        </div>}

      {/* Progreso de pacientes hacia el próximo premio */}
      <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-4" data-id="element-4008" data-mp="7d0x3nysrs4u">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-4009" data-mp="cblyvrtp6jve">
          Progreso de pacientes hacia su próximo premio
        </p>
        {patients.filter(p => completedVisits(p.id) > 0).length === 0 ? <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4010" data-mp="26otu3kll4xhk">
            Aún no hay visitas completadas registradas.
          </p> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-4011" data-mp="3r5tb1hta9nu9">
            {patients.filter(p => completedVisits(p.id) > 0).sort((a, b) => completedVisits(b.id) % VISITS_REQUIRED - completedVisits(a.id) % VISITS_REQUIRED).slice(0, 6).map(p => {
          const visits = completedVisits(p.id) % VISITS_REQUIRED;
          const pct = Math.round(visits / VISITS_REQUIRED * 100);
          return <div key={p.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800/40" data-id="element-4012" data-mp="nr70qxlg53hr">
                  
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-4013" data-mp="iq1ad6f9dsbe">
                    {p.name}
                  </p>
                  <div className="mt-2 h-2 w-full rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden" data-id="element-4014" data-mp="2kk8vfat7iyv2">
                    <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" style={{
                width: `${pct}%`
              }} data-id="element-4015" data-mp="3a8o0xvwe4mej" />
                  
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-4016" data-mp="3j6pyg1gx0wx2">
                    {visits}/{VISITS_REQUIRED} visitas
                  </p>
                </div>;
        })}
          </div>}
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center" data-id="element-4017" data-mp="1267rnhe4r5x">
        <div className="w-full md:w-96 relative" data-id="element-4018" data-mp="10bmu964cz1u">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-4019" data-mp="5ufu6tjar93g" />
          <Input style={{
          paddingLeft: '2.5rem'
        }} placeholder="Buscar por paciente, servicio o código..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-4020" data-mp="arzfueibqo5f" />
          
        </div>
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-4021" data-mp="209d7y2mx74kd">
          {statusTabs.map(tab => <button key={tab.key} onClick={() => setStatusFilter(tab.key as any)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${statusFilter === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-4022" data-mp="1vcczxm6ifrqm">
            
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* Grid de tarjetas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4023" data-mp="36pynj2wq1la1">
        {loading ? <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-4024" data-mp="31yqdq5rhbxwc">
            Cargando tarjetas...
          </div> : filteredCards.length === 0 ? <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-4025" data-mp="2rfsai2ss4kbl">
            <div className="flex flex-col items-center justify-center" data-id="element-4026" data-mp="2ciswnx7c3v8o">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-4027" data-mp="2ckvuh3eqasny">
                <GiftIcon className="w-8 h-8 text-gray-400" data-id="element-4028" data-mp="357ghxss32km8" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-4029" data-mp="359jhb3f4lq3m">
                No se encontraron tarjetas
              </h3>
              <p className="text-sm" data-id="element-4030" data-mp="2vif4oacxahby">
                {searchTerm || statusFilter !== 'todas' ? 'Intenta con otros filtros de búsqueda' : 'Aún no hay tarjetas de fidelidad emitidas'}
              </p>
            </div>
          </div> : filteredCards.map(card => {
        const patient = patients.find(item => item.id === card.patient_id);
        const meta = statusMeta(card.status);
        return <div key={card.id} className="group relative rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-4 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-id="element-4031" data-mp="1hfzcooikzfxw">
              
                <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full bg-emerald-50 dark:bg-emerald-900/10 group-hover:scale-110 transition-transform" data-id="element-4032" data-mp="1m9ts3fr5l8r6" />
                <div className="relative z-10 flex items-start gap-3" data-id="element-4033" data-mp="tk3d74hp62u0">
                  <div className="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex-shrink-0" data-id="element-4034" data-mp="tm7oi7hxe44b">
                    <AwardIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" data-id="element-4035" data-mp="17hrygdfofbzl" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4036" data-mp="2iz1c2jc4j9sz">
                    <div className="flex items-center justify-between gap-2" data-id="element-4037" data-mp="2ixq8ajioi21v">
                      <p className="font-semibold text-gray-900 dark:text-white truncate flex items-center gap-1.5" data-id="element-4038" data-mp="2ey7ojr2lfx0m">
                        <UserIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-4039" data-mp="1blqx01b4jk33" />
                        {patient?.name || 'Paciente eliminado'}
                      </p>
                      <Badge variant={meta.variant} data-id="element-4040" data-mp="1lkghm1uenin1">{meta.label}</Badge>
                    </div>
                    <p className="mt-1 font-mono text-xs text-gray-400" data-id="element-4041" data-mp="1pml6kn9wrr3c">
                      {card.code}
                    </p>
                    <div className="mt-3 space-y-1" data-id="element-4042" data-mp="sxlzhbl28vcq">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-4043" data-mp="xuvt73fhft6d">
                        Opciones de servicio gratis:
                      </p>
                      <div className="flex flex-wrap gap-1" data-id="element-4044" data-mp="11uw96si8ccfj">
                        {card.service_ids.map(id => <span key={id} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300" data-id="element-4045" data-mp="a5jbtga04esz">
                        
                            {serviceName(id)}
                          </span>)}
                      </div>
                    </div>
                    <p className="mt-3 text-xs text-gray-400" data-id="element-4046" data-mp="1mg1a0a9ro58">
                      Emitida: {formatDate(card.earned_at)}
                      {card.redeemed_at && ` · Canjeada: ${formatDate(card.redeemed_at)}`}
                    </p>
                  </div>
                </div>
              </div>;
      })}
      </div>

      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Nueva tarjeta de fidelidad" accentColor="emerald" size="lg" footer={<div className="flex justify-end gap-2" data-id="element-4048" data-mp="363jwm0dbmds3">
            <Button variant="secondary" onClick={() => setIsFormOpen(false)} data-id="element-4049" data-mp="360nzge9art4p">
              Cancelar
            </Button>
            <Button onClick={createCard} disabled={readOnly || !patientId || serviceIds.length !== 1} data-id="element-4050" data-mp="2w4kvd2uvxkf4">
            
              <CheckCircleIcon className="w-4 h-4" data-id="element-4051" data-mp="nwxw2iixl5va" /> Emitir tarjeta
            </Button>
          </div>} data-id="element-4047" data-mp="3f36y0m61vjl5">
        
        <div className="space-y-5" data-id="element-4052" data-mp="su6jaxixs03e">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200" data-id="element-4053" data-mp="ev6rjh04n530">
            Selecciona el paciente, la fecha de emisión y el servicio gratuito
            que podrá canjear al completar una cita.
          </div>
          <Select label="Paciente" value={patientId} onChange={setPatientId} options={patients.map(patient => ({
          value: patient.id,
          label: patient.name
        }))} data-id="element-4054" data-mp="9wde1okube2u" />

          <div data-id="element-4055" data-mp="47qcr1mfitg">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4056" data-mp="3ibx4kfu4mie7">
              Fecha de emisión
            </label>
            <input type="date" value={earnedAt} onChange={e => setEarnedAt(e.target.value)} className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-id="element-4057" data-mp="3n9ow38qv02hv" />
          </div>

          <div data-id="element-4058" data-mp="38nru4n9rlx4e">
            <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4059" data-mp="38n9o4yxaw4ja">
              Servicio que podrá canjear ({serviceIds.length}/1)
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" data-id="element-4060" data-mp="3hp973wrvliov">
              {loyaltyEligibleServices.length === 0 && <p className="col-span-3 text-sm text-gray-500 dark:text-gray-400" data-id="element-4061" data-mp="8rdcpfz6avz">
                  No se encontraron los servicios "Masaje Relax", "Detox
                  Facial" o "Pediluvio" en tu catálogo de servicios.
                </p>}
              {loyaltyEligibleServices.map(service => <label key={service.id} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2 text-sm transition-colors ${serviceIds.includes(service.id) ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'}`} data-id="element-4062" data-mp="58vt2d9271l5">
                
                  <input type="radio" name="loyalty-service" checked={serviceIds.includes(service.id)} onChange={() => selectService(service.id)} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500" data-id="element-4063" data-mp="lt7h0ynw46s7" />
                
                  {service.name}
                </label>)}
            </div>
          </div>
        </div>
      </Modal>
    </div>;
}